import { useState, useEffect, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip,
} from 'recharts';
import { toast } from 'sonner';
import {
  HeartPulse, Plus, Trash2, Home, CreditCard, Car, Briefcase, Wallet,
  TrendingDown, Lightbulb, AlertTriangle, TrendingUp, Lock, Sparkles,
} from 'lucide-react';

const fmt = (v) => `$${(Number(v) || 0).toLocaleString('en-AU', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const INCOME_FREQS = [
  { v: 'monthly', label: 'Monthly' }, { v: 'fortnightly', label: 'Fortnightly' },
  { v: 'weekly', label: 'Weekly' }, { v: 'yearly', label: 'Yearly' },
];
const LIAB_TYPES = [
  { v: 'home_loan', label: 'Home Loan', icon: Home, color: 'text-blue-600' },
  { v: 'car_loan', label: 'Car Loan', icon: Car, color: 'text-cyan-600' },
  { v: 'credit_card', label: 'Credit Card', icon: CreditCard, color: 'text-rose-600' },
  { v: 'personal_loan', label: 'Personal Loan', icon: Briefcase, color: 'text-purple-600' },
  { v: 'other', label: 'Other', icon: Wallet, color: 'text-slate-600' },
];
const LIAB_PIE_COLORS = {
  home_loan: '#3b82f6', car_loan: '#06b6d4', credit_card: '#f43f5e',
  personal_loan: '#a855f7', other: '#64748b',
};
const LEVEL = {
  danger: 'bg-rose-50 border-rose-200 text-rose-900',
  warning: 'bg-amber-50 border-amber-200 text-amber-900',
  success: 'bg-emerald-50 border-emerald-200 text-emerald-900',
  info: 'bg-blue-50 border-blue-200 text-blue-900',
};
const LEVEL_ICON = {
  danger: AlertTriangle, warning: AlertTriangle, success: TrendingUp, info: Lightbulb,
};

export default function PersonalFinanceCard({ BACKEND_URL }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview'); // overview | income | liabilities | expenses

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/super-admin/personal-finance`, { credentials: 'include' });
      if (res.ok) setData(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [BACKEND_URL]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading || !data) {
    return <Card className="p-6"><div className="animate-pulse h-48 bg-slate-100 rounded" /></Card>;
  }

  const { totals, liabilities, incomes, expenses, insights } = data;
  const surplusPositive = totals.monthly_surplus >= 0;

  return (
    <Card className="p-6 border-l-4 border-l-indigo-500" data-testid="personal-finance-card">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-100 rounded-lg">
            <HeartPulse className="h-5 w-5 text-indigo-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Personal Finance Position</h3>
            <p className="text-xs text-slate-500 flex items-center gap-1">
              <Lock className="h-3 w-3" /> Private to you · combined into Cash Flow Position
            </p>
          </div>
        </div>
        <div className="flex gap-1 bg-slate-100 p-1 rounded-lg" data-testid="pf-subtabs">
          {['overview', 'income', 'liabilities', 'expenses'].map(t => (
            <button key={t} onClick={() => setTab(t)}
              className={`text-xs font-medium px-3 py-1.5 rounded-md capitalize transition-colors ${
                tab === t ? 'bg-white shadow-sm text-indigo-700' : 'text-slate-600 hover:text-slate-900'}`}
              data-testid={`pf-tab-${t}`}>
              {t}
            </button>
          ))}
        </div>
      </div>

      {tab === 'overview' && (
        <PFOverview totals={totals} liabilities={liabilities} insights={insights} surplusPositive={surplusPositive} />
      )}
      {tab === 'income' && (
        <PFList BACKEND_URL={BACKEND_URL} kind="income" items={incomes} fetchData={fetchData} />
      )}
      {tab === 'liabilities' && (
        <PFLiabilities BACKEND_URL={BACKEND_URL} liabilities={liabilities} fetchData={fetchData} />
      )}
      {tab === 'expenses' && (
        <PFList BACKEND_URL={BACKEND_URL} kind="expense" items={expenses} fetchData={fetchData} />
      )}
    </Card>
  );
}

function PFOverview({ totals, liabilities, insights, surplusPositive }) {
  const pieData = liabilities.map(l => ({
    name: l.name, value: l.balance, type: l.type,
  })).filter(d => d.value > 0);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Monthly Income" value={totals.monthly_income} positive icon={TrendingUp} />
        <Stat label="Monthly Outflow" value={totals.monthly_outflow} positive={false} icon={TrendingDown} />
        <Stat label="Monthly Surplus" value={totals.monthly_surplus} positive={surplusPositive} bold icon={Sparkles} />
        <Stat label="Total Liabilities" value={totals.total_liability_balance} muted icon={Wallet} />
      </div>

      {pieData.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div className="p-4 bg-slate-50 rounded-lg">
            <p className="text-xs font-semibold text-slate-700 mb-2">Liability Mix</p>
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} innerRadius={40}>
                  {pieData.map((d, i) => (
                    <Cell key={i} fill={LIAB_PIE_COLORS[d.type] || '#64748b'} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => fmt(v)} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-2">
            <p className="text-xs font-semibold text-slate-700 mb-2">Debt Payoff Snapshot</p>
            {liabilities.slice(0, 4).map(l => (
              <div key={l.id} className="p-2.5 bg-white border border-slate-200 rounded-lg flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-sm font-semibold truncate">{l.name}</p>
                  <p className="text-[11px] text-slate-500">
                    {fmt(l.balance)} @ {l.interest_rate_pct}% · {l.years_to_payoff != null ? `${l.years_to_payoff}y` : '∞'}
                  </p>
                </div>
                {l.is_emi_too_low && (
                  <span className="text-[10px] uppercase font-bold text-rose-600 bg-rose-50 px-2 py-0.5 rounded">
                    EMI low
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {insights && insights.length > 0 && (
        <div className="space-y-2 mt-4" data-testid="pf-insights">
          {insights.map((ins, i) => {
            const Icon = LEVEL_ICON[ins.level] || Lightbulb;
            return (
              <div key={i} className={`flex items-start gap-3 p-3 rounded-lg border-l-4 ${LEVEL[ins.level] || LEVEL.info}`}>
                <Icon className="h-4 w-4 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-semibold">{ins.title}</p>
                  <p className="text-xs mt-0.5 opacity-90">{ins.message}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, positive, muted, bold, icon: Icon }) {
  const colorClass = muted ? 'text-slate-700 bg-slate-50' : positive ? 'text-emerald-900 bg-emerald-50' : 'text-rose-900 bg-rose-50';
  return (
    <div className={`p-3 rounded-lg ${colorClass}`}>
      <div className="flex items-center gap-1 mb-1">
        {Icon && <Icon className="h-3 w-3 opacity-70" />}
        <p className="text-[10px] uppercase tracking-wider opacity-70">{label}</p>
      </div>
      <p className={bold ? 'text-xl font-black' : 'text-base font-bold'}>
        {value < 0 && '−'}{fmt(Math.abs(value))}
      </p>
    </div>
  );
}

function PFList({ BACKEND_URL, kind, items, fetchData }) {
  const [name, setName] = useState('');
  const [amount, setAmount] = useState('');
  const [frequency, setFrequency] = useState('monthly');
  const [editingId, setEditingId] = useState(null);
  const [category, setCategory] = useState('general');
  const endpoint = kind === 'income' ? 'income' : 'expense';

  const reset = () => { setName(''); setAmount(''); setFrequency('monthly'); setEditingId(null); setCategory('general'); };

  const handleSave = async () => {
    if (!name.trim()) { toast.error('Name required'); return; }
    const amt = parseFloat(amount);
    if (isNaN(amt) || amt < 0) { toast.error('Valid amount required'); return; }
    const body = { id: editingId || undefined, name: name.trim(), amount: amt, frequency };
    if (kind === 'expense') body.category = category;
    try {
      const res = await fetch(`${BACKEND_URL}/api/super-admin/personal-finance/${endpoint}`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) { toast.success(editingId ? 'Updated' : 'Added'); reset(); fetchData(); }
      else { const e = await res.json().catch(()=>({})); toast.error(e.detail || 'Failed'); }
    } catch (e) { toast.error('Failed'); }
  };
  const handleDelete = async (id) => {
    if (!confirm('Remove this item?')) return;
    const res = await fetch(`${BACKEND_URL}/api/super-admin/personal-finance/${endpoint}/${id}`, {
      method: 'DELETE', credentials: 'include',
    });
    if (res.ok) { toast.success('Removed'); fetchData(); reset(); }
    else toast.error('Failed');
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-2 p-3 bg-slate-50 rounded-lg border">
        <div className="md:col-span-4">
          <Label className="text-xs">Name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={kind === 'income' ? 'e.g. Salary, Rent income' : 'e.g. Groceries, School fees'} data-testid={`pf-${kind}-name`} />
        </div>
        {kind === 'expense' && (
          <div className="md:col-span-3">
            <Label className="text-xs">Category</Label>
            <Input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="e.g. groceries" />
          </div>
        )}
        <div className={kind === 'expense' ? 'md:col-span-2' : 'md:col-span-3'}>
          <Label className="text-xs">Amount ($)</Label>
          <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid={`pf-${kind}-amount`} />
        </div>
        <div className={kind === 'expense' ? 'md:col-span-2' : 'md:col-span-3'}>
          <Label className="text-xs">Frequency</Label>
          <Select value={frequency} onValueChange={setFrequency}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{INCOME_FREQS.map(f => <SelectItem key={f.v} value={f.v}>{f.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="md:col-span-1 flex items-end gap-1">
          <Button onClick={handleSave} className="w-full" data-testid={`pf-${kind}-save`}>
            {editingId ? '✓' : <Plus className="h-4 w-4" />}
          </Button>
          {editingId && <Button variant="outline" size="sm" onClick={reset}>×</Button>}
        </div>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-6 text-sm text-slate-500 border-2 border-dashed rounded-lg">
          No {kind === 'income' ? 'income sources' : 'expenses'} yet.
        </div>
      ) : (
        <div className="space-y-1.5">
          {items.map(it => (
            <div key={it.id} className="flex items-center justify-between p-2.5 bg-white border rounded-lg" data-testid={`pf-${kind}-item-${it.id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate">{it.name}{it.category && it.category !== 'general' && <span className="text-xs text-slate-500 ml-2">· {it.category}</span>}</p>
                <p className="text-[11px] text-slate-500">{fmt(it.amount)} / {it.frequency}</p>
              </div>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => {
                  setEditingId(it.id); setName(it.name); setAmount(it.amount); setFrequency(it.frequency);
                  if (kind === 'expense' && it.category) setCategory(it.category);
                }}>Edit</Button>
                <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-600" onClick={() => handleDelete(it.id)}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PFLiabilities({ BACKEND_URL, liabilities, fetchData }) {
  const [form, setForm] = useState({ id: null, name: '', type: 'home_loan', balance: '', monthly_emi: '', interest_rate_pct: '', lender: '' });
  const reset = () => setForm({ id: null, name: '', type: 'home_loan', balance: '', monthly_emi: '', interest_rate_pct: '', lender: '' });

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name required'); return; }
    const body = {
      id: form.id || undefined,
      name: form.name.trim(), type: form.type, lender: form.lender,
      balance: parseFloat(form.balance) || 0,
      monthly_emi: parseFloat(form.monthly_emi) || 0,
      interest_rate_pct: parseFloat(form.interest_rate_pct) || 0,
    };
    const res = await fetch(`${BACKEND_URL}/api/super-admin/personal-finance/liability`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (res.ok) { toast.success(form.id ? 'Updated' : 'Added'); reset(); fetchData(); }
    else { const e = await res.json().catch(()=>({})); toast.error(e.detail || 'Failed'); }
  };
  const handleDelete = async (id) => {
    if (!confirm('Remove this liability?')) return;
    const res = await fetch(`${BACKEND_URL}/api/super-admin/personal-finance/liability/${id}`, {
      method: 'DELETE', credentials: 'include',
    });
    if (res.ok) { toast.success('Removed'); fetchData(); reset(); } else toast.error('Failed');
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-2 p-3 bg-slate-50 rounded-lg border">
        <div className="md:col-span-3">
          <Label className="text-xs">Name</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. CBA Home Loan" data-testid="pf-liab-name" />
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs">Type</Label>
          <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{LIAB_TYPES.map(t => <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs">Balance ($)</Label>
          <Input type="number" value={form.balance} onChange={(e) => setForm({ ...form, balance: e.target.value })} data-testid="pf-liab-balance" />
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs">EMI / month ($)</Label>
          <Input type="number" value={form.monthly_emi} onChange={(e) => setForm({ ...form, monthly_emi: e.target.value })} data-testid="pf-liab-emi" />
        </div>
        <div className="md:col-span-1">
          <Label className="text-xs">Rate %</Label>
          <Input type="number" step="0.01" value={form.interest_rate_pct} onChange={(e) => setForm({ ...form, interest_rate_pct: e.target.value })} data-testid="pf-liab-rate" />
        </div>
        <div className="md:col-span-1">
          <Label className="text-xs">Lender</Label>
          <Input value={form.lender} onChange={(e) => setForm({ ...form, lender: e.target.value })} placeholder="CBA" />
        </div>
        <div className="md:col-span-1 flex items-end gap-1">
          <Button onClick={handleSave} className="w-full" data-testid="pf-liab-save">
            {form.id ? '✓' : <Plus className="h-4 w-4" />}
          </Button>
          {form.id && <Button variant="outline" size="sm" onClick={reset}>×</Button>}
        </div>
      </div>

      {liabilities.length === 0 ? (
        <div className="text-center py-6 text-sm text-slate-500 border-2 border-dashed rounded-lg">
          No liabilities yet. Add your home loan, car loan, credit cards, etc.
        </div>
      ) : (
        <div className="space-y-2">
          {liabilities.map(l => {
            const T = LIAB_TYPES.find(x => x.v === l.type) || LIAB_TYPES[LIAB_TYPES.length - 1];
            const Icon = T.icon;
            return (
              <div key={l.id} className="p-3 bg-white border-l-4 border-l-indigo-300 border rounded-lg" data-testid={`pf-liab-item-${l.id}`}>
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <Icon className={`h-4 w-4 ${T.color}`} />
                    <p className="font-semibold">{l.name}</p>
                    {l.lender && <span className="text-xs text-slate-500">· {l.lender}</span>}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setForm({
                      id: l.id, name: l.name, type: l.type, balance: l.balance,
                      monthly_emi: l.monthly_emi, interest_rate_pct: l.interest_rate_pct, lender: l.lender || '',
                    })}>Edit</Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-600" onClick={() => handleDelete(l.id)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
                  <Mini label="Balance" value={fmt(l.balance)} />
                  <Mini label="EMI/month" value={fmt(l.monthly_emi)} />
                  <Mini label="Rate" value={`${l.interest_rate_pct}%`} />
                  <Mini label="Years to clear" value={l.years_to_payoff != null ? `${l.years_to_payoff}y` : '∞'}
                    danger={l.is_emi_too_low} />
                  <Mini label="Total interest" value={fmt(l.total_interest_remaining)} danger={l.total_interest_remaining > l.balance} />
                </div>
                {l.is_emi_too_low && (
                  <p className="text-xs text-rose-700 mt-2 bg-rose-50 px-2 py-1 rounded border border-rose-200">
                    ⚠️ Your EMI ({fmt(l.monthly_emi)}) is below the monthly interest ({fmt(l.monthly_interest)}). Balance is growing each month — increase EMI urgently.
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Mini({ label, value, danger }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-slate-500">{label}</p>
      <p className={`font-semibold ${danger ? 'text-rose-700' : 'text-slate-800'}`}>{value}</p>
    </div>
  );
}
